import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, SectionHeading } from '@/components/ui';
import { apiFetch, ApiError } from '@/lib/api/client';

interface Application {
  id: string;
  status: string;
  legal_name: string | null;
  headline: string | null;
  bio: string | null;
  applied_at: string | null;
  applied_via_code: string | null;
  profiles: {
    display_name: string;
    languages: string[];
    city: string | null;
    date_of_birth: string | null;
  };
}

interface Invite {
  code: string;
  note: string | null;
  max_uses: number;
  used_count: number;
  expires_at: string | null;
  revoked_at: string | null;
}

/**
 * Minimal admin surface — Phase 1 only.
 *
 * Exists so a companion application can be reviewed end-to-end without
 * hand-editing the database. The full panel (payouts, live sessions, reports,
 * audit log) is Phase 9. There is no admin signup: the role is granted by SQL.
 */
export default function Admin() {
  const [applications, setApplications] = useState<Application[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actingOn, setActingOn] = useState<string | null>(null);

  const [newCode, setNewCode] = useState('');
  const [newNote, setNewNote] = useState('');
  const [creating, setCreating] = useState(false);

  // Tracked separately from `applications.length` so a failed load never
  // renders as "nothing waiting" — an empty queue and a broken query must not
  // look the same.
  const [loadedOk, setLoadedOk] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [apps, inv] = await Promise.all([
        apiFetch<{ applications: Application[] }>('/api/admin/applications'),
        apiFetch<{ invites: Invite[] }>('/api/admin/invites'),
      ]);
      setApplications(apps.applications);
      setInvites(inv.invites);
      setLoadedOk(true);
    } catch (err) {
      setLoadedOk(false);
      setError(err instanceof ApiError ? err.message : 'Could not load.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Decide an application.
   *
   * `applicantMessage` is what the applicant reads on their profile page;
   * internal notes are a separate field they never see. Rejecting without a
   * message is refused by the API — project.md §26 requires the applicant to
   * be able to see why.
   */
  const decide = async (
    id: string,
    decision: 'approve' | 'reject' | 'request_info',
    applicantMessage?: string,
  ) => {
    if (actingOn) return;
    setActingOn(id);
    setError('');
    try {
      await apiFetch(`/api/admin/applications/${id}/decision`, {
        method: 'POST',
        body: { decision, applicantMessage },
      });

      // request_info keeps the application in the queue — the applicant has to
      // come back with more, and it should still be waiting when they do.
      if (decision !== 'request_info') {
        setApplications((prev) => prev.filter((a) => a.id !== id));
      }
      return true;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that.');
      return false;
    } finally {
      setActingOn(null);
    }
  };

  const createInvite = async () => {
    if (!/^[A-Za-z0-9-]{4,24}$/.test(newCode.trim()) || creating) return;
    setCreating(true);
    setError('');
    try {
      await apiFetch('/api/admin/invites', {
        method: 'POST',
        body: { code: newCode.trim(), note: newNote.trim() || undefined, maxUses: 1 },
      });
      setNewCode('');
      setNewNote('');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create that code.');
    } finally {
      setCreating(false);
    }
  };

  const age = (dob: string | null) => {
    if (!dob) return null;
    const d = new Date(dob);
    return Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000));
  };

  const field =
    'w-full rounded-xl border border-[var(--line)] bg-[var(--surface)] px-3.5 py-2.5 text-sm';

  return (
    <div className="flex flex-col gap-12">
      <header className="animate-rise">
        <p className="eyebrow">Internal</p>
        <h1 className="mt-2 text-3xl font-semibold">Admin</h1>
        <p className="mt-2 text-[var(--ink-2)]">
          Companion applications and invite codes. Everything else lands in Phase 9.
        </p>
      </header>

      {error && (
        <div
          className="rounded-xl border-l-[3px] px-4 py-3 text-sm"
          style={{ borderColor: 'var(--danger)', background: 'var(--danger-soft)' }}
        >
          {error}
        </div>
      )}

      {/* ── Applications ─────────────────────────────────────────────────── */}
      <section>
        <SectionHeading
          eyebrow={`${applications.length} waiting`}
          action={
            <button
              type="button"
              onClick={() => void load()}
              className="text-sm font-medium text-[var(--marigold)] hover:underline"
            >
              Refresh
            </button>
          }
        >
          Pending applications
        </SectionHeading>

        {loading ? (
          <p className="text-sm text-[var(--muted)]">Loading…</p>
        ) : !loadedOk ? (
          <Card className="px-6 py-14 text-center">
            <p className="font-medium" style={{ color: 'var(--danger)' }}>
              Could not load applications
            </p>
            <p className="mt-1.5 text-sm text-[var(--ink-2)]">
              This is a failure, not an empty queue. See the error above.
            </p>
            <div className="mt-5">
              <Button variant="secondary" size="sm" onClick={() => void load()}>
                Try again
              </Button>
            </div>
          </Card>
        ) : applications.length === 0 ? (
          <Card className="px-6 py-14 text-center">
            <p className="font-medium">Nothing waiting</p>
            <p className="mt-1.5 text-sm text-[var(--ink-2)]">
              New applications appear here as they arrive.
            </p>
          </Card>
        ) : (
          <div className="flex flex-col gap-4">
            {applications.map((a) => (
              <Card key={a.id} className="p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold">
                      {a.legal_name ?? a.profiles.display_name}
                    </h3>
                    <p className="mt-0.5 text-sm text-[var(--muted)]">
                      Goes by {a.profiles.display_name}
                      {age(a.profiles.date_of_birth) !== null && (
                        <> · {age(a.profiles.date_of_birth)} years old</>
                      )}
                      {a.profiles.city && <> · {a.profiles.city}</>}
                    </p>
                  </div>
                  {a.applied_via_code && <Badge tone="teal">code {a.applied_via_code}</Badge>}
                </div>

                {a.profiles.languages.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {a.profiles.languages.map((l) => (
                      <Badge key={l}>{l}</Badge>
                    ))}
                  </div>
                )}

                {a.bio && (
                  <p className="mt-4 text-sm leading-relaxed text-[var(--ink-2)]">{a.bio}</p>
                )}

                <ReviewActions
                  busy={actingOn === a.id}
                  appliedAt={a.applied_at}
                  onDecide={(d, msg) => decide(a.id, d, msg)}
                />
              </Card>
            ))}
          </div>
        )}

        <p className="mt-4 text-xs text-[var(--muted)]">
          Approving also changes the account&rsquo;s role to companion, which is what unlocks the
          dashboard.
        </p>
      </section>

      {/* ── Invites ──────────────────────────────────────────────────────── */}
      <section>
        <SectionHeading eyebrow="Curated supply">Invite codes</SectionHeading>

        <Card className="p-5">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-40 flex-1">
              <label className="eyebrow mb-2 block" htmlFor="code">
                New code
              </label>
              <input
                id="code"
                className={`${field} font-[family-name:var(--font-mono)] uppercase`}
                placeholder="MMP-ANANYA"
                value={newCode}
                onChange={(e) => setNewCode(e.target.value.toUpperCase())}
              />
            </div>
            <div className="min-w-40 flex-1">
              <label className="eyebrow mb-2 block" htmlFor="note">
                Note
              </label>
              <input
                id="note"
                className={field}
                placeholder="Who it is for"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
              />
            </div>
            <Button onClick={() => void createInvite()} disabled={creating}>
              {creating ? 'Creating…' : 'Create'}
            </Button>
          </div>
          <p className="mt-3 text-xs text-[var(--muted)]">
            Single use, expires in 90 days. Letters, numbers and hyphens only.
          </p>
        </Card>

        {invites.length > 0 && (
          <Card className="mt-4 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] text-sm">
                <thead>
                  <tr
                    className="border-b border-[var(--line)] text-left"
                    style={{ background: 'var(--surface-2)' }}
                  >
                    <th className="eyebrow px-5 py-3">Code</th>
                    <th className="eyebrow px-5 py-3">Note</th>
                    <th className="eyebrow px-5 py-3 text-right">Used</th>
                    <th className="eyebrow px-5 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map((i) => {
                    const spent = i.used_count >= i.max_uses;
                    const expired = i.expires_at !== null && new Date(i.expires_at) < new Date();
                    const dead = spent || expired || i.revoked_at !== null;
                    return (
                      <tr key={i.code} className="border-b border-[var(--line-soft)] last:border-0">
                        <td className="px-5 py-3 font-[family-name:var(--font-mono)] font-semibold">
                          {i.code}
                        </td>
                        <td className="px-5 py-3 text-[var(--ink-2)]">{i.note ?? '—'}</td>
                        <td className="px-5 py-3 text-right tabular-nums">
                          {i.used_count}/{i.max_uses}
                        </td>
                        <td className="px-5 py-3">
                          <Badge tone={dead ? 'neutral' : 'online'}>
                            {i.revoked_at
                              ? 'revoked'
                              : expired
                                ? 'expired'
                                : spent
                                  ? 'used'
                                  : 'active'}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>
    </div>
  );
}

/**
 * Approve, reject, or ask for more.
 *
 * Reject and "ask for more" both open a message box first, because both send
 * something the applicant will read. Approve does not — nobody needs a reason
 * for good news, and an extra click on the common path is friction for
 * nothing.
 */
function ReviewActions({
  busy,
  appliedAt,
  onDecide,
}: {
  busy: boolean;
  appliedAt: string | null;
  onDecide: (
    decision: 'approve' | 'reject' | 'request_info',
    applicantMessage?: string,
  ) => Promise<boolean | undefined>;
}) {
  const [mode, setMode] = useState<'reject' | 'request_info' | null>(null);
  const [message, setMessage] = useState('');

  const copy = {
    reject: {
      title: 'Why are you rejecting this?',
      hint: 'The applicant sees this on their profile page. Be specific enough that they know whether to reapply.',
      action: 'Confirm rejection',
    },
    request_info: {
      title: 'What do you need from them?',
      hint: 'The application stays in the queue. They see this on their profile page.',
      action: 'Send request',
    },
  };

  if (mode) {
    const c = copy[mode];
    return (
      <div className="mt-5 border-t border-[var(--line-soft)] pt-4">
        <p className="text-sm font-medium">{c.title}</p>
        <p className="mt-1 text-xs text-[var(--muted)]">{c.hint}</p>

        <textarea
          autoFocus
          rows={3}
          maxLength={1000}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="mt-3 w-full rounded-xl px-3.5 py-2.5 text-sm outline-none"
          style={{
            background: 'var(--surface)',
            color: 'var(--ink)',
            border: '1px solid var(--line)',
          }}
          placeholder={
            mode === 'reject'
              ? 'The ID photo did not match the profile photo.'
              : 'Please upload a clearer photo of your ID.'
          }
        />

        <div className="mt-3 flex gap-2">
          <Button
            size="sm"
            disabled={busy || message.trim().length === 0}
            onClick={() => {
              void onDecide(mode, message.trim()).then((ok) => {
                if (ok) {
                  setMode(null);
                  setMessage('');
                }
              });
            }}
          >
            {busy ? 'Saving…' : c.action}
          </Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => setMode(null)}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-[var(--line-soft)] pt-4">
      <Button size="sm" disabled={busy} onClick={() => void onDecide('approve')}>
        {busy ? 'Saving…' : 'Approve'}
      </Button>
      <Button size="sm" variant="secondary" disabled={busy} onClick={() => setMode('request_info')}>
        Ask for more
      </Button>
      <Button size="sm" variant="ghost" disabled={busy} onClick={() => setMode('reject')}>
        Reject
      </Button>
      {appliedAt && (
        <span className="ml-auto text-xs text-[var(--muted)]">
          Applied {new Date(appliedAt).toLocaleDateString('en-IN')}
        </span>
      )}
    </div>
  );
}
